import { useEffect, useMemo, useState } from "react";
import { BatteryMedium, CircleGauge, Radio, Thermometer, X } from "lucide-react";
import { listVehicleTires } from "../api";
import type { TireState, Vehicle } from "../types";

const positionLabel: Record<string, string> = {
  axle_1_left: "Dirección izquierda",
  axle_1_right: "Dirección derecha",
  axle_2_left_inner: "Eje 2 izquierda interior",
  axle_2_left_outer: "Eje 2 izquierda exterior",
  axle_2_right_inner: "Eje 2 derecha interior",
  axle_2_right_outer: "Eje 2 derecha exterior",
  axle_3_left_inner: "Eje 3 izquierda interior",
  axle_3_left_outer: "Eje 3 izquierda exterior",
  axle_3_right_inner: "Eje 3 derecha interior",
  axle_3_right_outer: "Eje 3 derecha exterior",
};

export function TirePanel({
  open,
  onClose,
  vehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicle: Vehicle;
}) {
  const [tires, setTires] = useState<TireState[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listVehicleTires(vehicle.id)
        .then(setTires)
        .catch((reason) => setError(reason instanceof Error ? reason.message : "No fue posible cargar TPMS"));
    };
    refresh();
    window.addEventListener("gsf:tpms-updated", refresh);
    return () => window.removeEventListener("gsf:tpms-updated", refresh);
  }, [open, vehicle.id]);

  const critical = useMemo(
    () => tires.filter((tire) => tire.severity === "critical").length,
    [tires],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel tire-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tire-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">TPMS · POSICIÓN FÍSICA</span>
            <h2 id="tire-title">{vehicle.unit_code} · Salud de llantas</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar TPMS"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><CircleGauge size={17} /><b>{tires.length}</b> sensores</span>
          <span><Radio size={17} /><b>{tires.filter((tire) => tire.severity !== "offline").length}</b> comunicando</span>
          <span><Thermometer size={17} /><b>{critical}</b> críticas</span>
        </section>
        <div className="tire-panel__body">
          <div className="tire-vehicle-map">
            <div className="tire-cab">TRACTO</div>
            {[1, 2, 3].map((axle) => (
              <section className={`tire-axle tire-axle--${axle}`} key={axle}>
                <b>EJE {axle}</b>
                <div>
                  {tires.filter((tire) => tire.axle === axle && tire.side === "left").map((tire) => (
                    <TireCard tire={tire} key={tire.installation_id} />
                  ))}
                </div>
                <i />
                <div>
                  {tires.filter((tire) => tire.axle === axle && tire.side === "right").map((tire) => (
                    <TireCard tire={tire} key={tire.installation_id} />
                  ))}
                </div>
              </section>
            ))}
          </div>
          <aside className="tire-detail-list">
            <span className="eyebrow">INVENTARIO INSTALADO</span>
            {tires.map((tire) => (
              <article key={tire.installation_id}>
                <i className={`status-dot is-${tire.severity}`} />
                <span>
                  <strong>{positionLabel[tire.wheel_position] || tire.wheel_position}</strong>
                  <small>{tire.sensor_uid}</small>
                </span>
                <b>{tire.pressure_psi ?? "—"} PSI</b>
              </article>
            ))}
          </aside>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}

function TireCard({ tire }: { tire: TireState }) {
  return (
    <article className={`tire-card tire-card--${tire.severity}`} title={positionLabel[tire.wheel_position]}>
      <span><b>{tire.pressure_psi ?? "—"}</b><small>PSI</small></span>
      <span><Thermometer size={10} />{tire.temperature_c ?? "—"}°</span>
      <span><BatteryMedium size={10} />{tire.battery_percent ?? "—"}%</span>
    </article>
  );
}
